/**
 * Une activité et ses séances, en une carte.
 *
 * Partagée par le catalogue et par les pages de commune et de période. Elle y
 * a d'abord été écrite deux fois, à quelques lignes d'écart : deux cartes qui
 * disent la même chose finissent par ne plus la dire pareil, et c'est le genre
 * d'écart qu'on découvre en comparant deux captures six mois plus tard.
 *
 * Comme les vues qui l'emploient, elle ne lit rien : elle est rendue telle
 * quelle au build par `renderToString`.
 */

import { Link } from 'react-router-dom';
import { mentionDeDisponibilite, trancheDAge } from '../../lib/seo/stage';
import { libelleDeSeance, ouEtQuand, type Activite } from '../../lib/seo/catalogue';

const CarteDActivite = ({ activite }: { activite: Activite }) => {
  const { vitrine } = activite;

  return (
    <article className="border border-gray-200 rounded-xl overflow-hidden hover:border-primary-300 transition-colors">
      {vitrine.image_url && (
        <Link to={`/stages/${vitrine.slug}`} tabIndex={-1} aria-hidden="true">
          <img
            src={vitrine.image_url}
            /*
              Vide et masquée : le lien qui suit porte déjà le nom de
              l'activité, et le répéter ferait entendre deux fois la même chose
              à un lecteur d'écran.
            */
            alt=""
            className="w-full h-40 object-cover"
            loading="lazy"
          />
        </Link>
      )}

      <div className="p-4">
        <h3 className="font-semibold text-gray-900 leading-snug">
          <Link to={`/stages/${vitrine.slug}`} className="hover:text-primary-700">
            {activite.nom}
          </Link>
        </h3>
        <p className="text-sm text-gray-600 mt-0.5">
          {trancheDAge(vitrine.age_min, vitrine.age_max)}
        </p>
        <p className="text-sm text-gray-700 mt-2">{ouEtQuand(activite)}</p>

        {vitrine.description && (
          <p className="text-sm text-gray-600 mt-2 line-clamp-3">{vitrine.description}</p>
        )}

        {/*
          Les séances, en liens. C'est par elles qu'un robot atteint les 67
          pages du catalogue, et par elles qu'une famille choisit sa commune.
        */}
        <ul className="mt-3 flex flex-wrap gap-x-3 gap-y-1">
          {activite.seances.map((s) => (
            <li key={s.slug}>
              <Link
                to={`/stages/${s.slug}`}
                className="text-sm text-primary-700 hover:text-primary-900 hover:underline"
              >
                {libelleDeSeance(s)}
              </Link>
            </li>
          ))}
        </ul>

        <p className="text-xs text-gray-500 mt-3">{mentionDeDisponibilite(vitrine)}</p>
      </div>
    </article>
  );
};

export default CarteDActivite;
